/**
 * notchConfluence — detects when 2+ models fire on the same underlying
 * market within a rolling window. The confluence is what makes any single
 * notch more actionable: one model calling a setup is a data point; three
 * models calling the same setup within a week is a regime read.
 *
 * Symbol → market normalization: COT publishes on the futures contract
 * (ES/NQ/GC); GEX / IV / AVWAP publish on the tradable (SPY/QQQ/GLD).
 * Both are readings on the SAME underlying market and belong together in
 * the confluence view.
 */

import { UnifiedRow, ScorecardModel, ScorecardOutcome } from './notchesScorecardService';

export const CONFLUENCE_WINDOW_DAYS = 7;
export const CONFLUENCE_MIN_MODELS = 2;

const SYMBOL_TO_MARKET: Record<string, string> = {
  ES: 'S&P 500', MES: 'S&P 500', SPX: 'S&P 500', SPY: 'S&P 500',
  NQ: 'Nasdaq-100', MNQ: 'Nasdaq-100', QQQ: 'Nasdaq-100',
  IWM: 'Russell 2000',
  GC: 'Gold', GLD: 'Gold',
  CL: 'Crude Oil',
  XLE: 'Energy', XLF: 'Financials', XLK: 'Technology',
  XLV: 'Health Care', XLU: 'Utilities',
};

export function marketOf(symbol: string): string {
  return SYMBOL_TO_MARKET[symbol] || symbol;
}

export interface ConfluenceMember {
  model: ScorecardModel;
  symbol: string;
  date: string;
  reading: string;
  outcome: ScorecardOutcome;
  tag: string;
}

export interface ConfluenceEvent {
  market: string;
  startDate: string;                 // earliest member date
  endDate: string;                   // latest member date
  members: ConfluenceMember[];       // 2+ notches in this window
  distinctModels: ScorecardModel[];  // unique model list for the pill count
  hasOpen: boolean;                  // at least one in-flight member
}

// Sliding window: for each market, walk rows in date order. Anchor at row i;
// collect all rows within CONFLUENCE_WINDOW_DAYS after it. If >=2 distinct
// models are present, emit an event and skip past those rows so we don't
// emit overlapping events for the same cluster.
export function detectConfluence(rows: UnifiedRow[]): ConfluenceEvent[] {
  const byMarket = new Map<string, UnifiedRow[]>();
  for (const r of rows) {
    const m = marketOf(r.symbol);
    if (!byMarket.has(m)) byMarket.set(m, []);
    byMarket.get(m)!.push(r);
  }

  const events: ConfluenceEvent[] = [];
  const entries = Array.from(byMarket.entries());
  for (const [market, marketRows] of entries) {
    const sorted = marketRows.slice().sort((a, b) => a.date.localeCompare(b.date));
    let i = 0;
    while (i < sorted.length) {
      const anchor = sorted[i];
      const anchorMs = new Date(`${anchor.date}T00:00:00Z`).getTime();
      let j = i;
      while (j < sorted.length) {
        const gap = (new Date(`${sorted[j].date}T00:00:00Z`).getTime() - anchorMs) / 86400000;
        if (gap > CONFLUENCE_WINDOW_DAYS) break;
        j++;
      }
      const window = sorted.slice(i, j);
      const modelSet = new Set(window.map((r) => r.model));
      if (modelSet.size >= CONFLUENCE_MIN_MODELS) {
        events.push({
          market,
          startDate: window[0].date,
          endDate: window[window.length - 1].date,
          members: window.map((r) => ({
            model: r.model, symbol: r.symbol, date: r.date,
            reading: r.reading, outcome: r.outcome, tag: r.tag,
          })),
          distinctModels: Array.from(modelSet),
          hasOpen: window.some((r) => r.outcome === 'open'),
        });
        i = j;   // skip past the whole cluster
      } else {
        i++;
      }
    }
  }
  return events.sort((a, b) => {
    if (a.hasOpen !== b.hasOpen) return a.hasOpen ? -1 : 1;
    return b.endDate.localeCompare(a.endDate);
  });
}

// Return true if the given row participated in any confluence event.
// Used by the reading log to render a small "Confluence" pill on member rows.
export function isConfluenceMember(
  row: UnifiedRow, events: ConfluenceEvent[],
): boolean {
  for (const e of events) {
    if (marketOf(row.symbol) !== e.market) continue;
    for (const m of e.members) {
      if (m.model === row.model && m.symbol === row.symbol && m.date === row.date) return true;
    }
  }
  return false;
}
